import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../services/api';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { Loader2, AlertCircle, FileText, Plus, Trash2, Edit2, Search, X } from 'lucide-react';
import { toast } from 'react-toastify';

const AdminPdfs = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPdf, setEditingPdf] = useState(null);

  // Dropdown data state
  const [years, setYears] = useState([]);
  const [branches, setBranches] = useState([]);
  const [semesters, setSemesters] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    pdfUrl: '',
    year: '',
    branch: '',
    semester: '',
    subject: '',
    type: "PYQ'S",
    subtype: ''
  });

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchPdfs();
      fetchDropdownData();
    } else {
      setLoading(false);
      setError('Unauthorized: admin only');
    }
  }, [user]);

  const fetchDropdownData = async () => {
    try {
      const res = await axios.get(API.GET_DATA_LOOKUP);
      if (res.data) {
        // Extract unique years, branches, and semesters
        const uniqueYears = [...new Set(res.data.map(item => item.year).filter(Boolean))].sort();
        const uniqueBranches = [...new Set(res.data.map(item => item.branch).filter(Boolean))].sort();
        const uniqueSemesters = [...new Set(res.data.map(item => item.semester).filter(Boolean))].sort((a, b) => parseInt(a) - parseInt(b));
        
        setYears(uniqueYears);
        setBranches(uniqueBranches);
        setSemesters(uniqueSemesters);
      }
    } catch (err) {
      console.error('Failed to fetch dropdown data:', err);
      // Set default values if fetch fails
      setYears(['Second Year', 'Third Year', 'Fourth Year']);
      setBranches(['Computer', 'IT']);
      setSemesters(['semester 1', 'semester 2']);
    }
  };

  const fetchPdfs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API.GET_PDFS);
      if (res.data) {
        setPdfs(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load PDFs');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pdfUrl || !formData.year || !formData.branch || !formData.semester || !formData.subject || !formData.type || !formData.subtype) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (editingPdf) {
        // Update existing PDF
        const res = await axios.put(
          `${API.GET_PDF_BY_ID(editingPdf._id)}`,
          formData,
          { withCredentials: true }
        );
        if (res.data.success) {
          toast.success('PDF updated successfully');
          setEditingPdf(null);
        }
      } else {
        // Add new PDF
        const res = await axios.post(
          API.UPLOAD,
          formData,
          { withCredentials: true }
        );
        if (res.data.success) {
          toast.success('PDF added successfully');
        }
      }
      
      // Reset form and refresh list
      setFormData({
        pdfUrl: '',
        year: '',
        branch: '',
        semester: '',
        subject: '',
        type: "PYQ'S",
        subtype: ''
      });
      setShowAddForm(false);
      fetchPdfs();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save PDF');
    }
  };

  const handleEdit = (pdf) => {
    setEditingPdf(pdf);
    setFormData({
      pdfUrl: pdf.pdfUrl,
      year: pdf.year,
      branch: pdf.branch,
      semester: pdf.semester,
      subject: pdf.subject,
      type: pdf.type,
      subtype: pdf.subtype
    });
    setShowAddForm(true);
  };

  const handleDelete = async (pdfId) => {
    if (!window.confirm('Are you sure you want to delete this PDF?')) {
      return;
    }

    try {
      const res = await axios.delete(
        `${API.GET_PDF_BY_ID(pdfId)}`,
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success('PDF deleted successfully');
        fetchPdfs();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to delete PDF');
    }
  };

  const handleCancelEdit = () => {
    setEditingPdf(null);
    setShowAddForm(false);
    setFormData({
      pdfUrl: '',
      year: '',
      branch: '',
      semester: '',
      subject: '',
      type: "PYQ'S",
      subtype: ''
    });
  };

  // Filter PDFs based on search query
  const filteredPdfs = pdfs.filter(pdf => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (pdf.year || '').toLowerCase().includes(query) ||
      (pdf.branch || '').toLowerCase().includes(query) ||
      (pdf.semester || '').toLowerCase().includes(query) ||
      (pdf.subject || '').toLowerCase().includes(query) ||
      (pdf.type || '').toLowerCase().includes(query) ||
      (pdf.subtype || '').toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-300 text-lg font-medium">Loading PDFs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 px-4">
        <div className="text-center max-w-md">
          <div className="p-3 rounded-full bg-red-500/10 border border-red-500/20 w-fit mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Error Loading PDFs</h2>
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Manage PDFs - Admin Panel | ReferMe"
        description="Admin panel for managing PDF resources"
        keywords="admin, manage pdfs, insem notes"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <FileText className="w-8 h-8 text-indigo-500" />
                Manage PDFs
              </h1>
              <p className="text-gray-400 mt-1">Add, edit, or delete PDF resources</p>
            </div>
            
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium"
            >
              {showAddForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {showAddForm ? 'Cancel' : 'Add New PDF'}
            </button>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">
                {editingPdf ? 'Edit PDF' : 'Add New PDF'}
              </h2>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-gray-300 mb-2 font-medium">PDF URL *</label>
                  <input
                    type="url"
                    name="pdfUrl"
                    value={formData.pdfUrl}
                    onChange={handleInputChange}
                    placeholder="https://drive.google.com/..."
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">Year *</label>
                  <select
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                    required
                  >
                    <option value="">Select Year</option>
                    {years.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">Branch *</label>
                  <select
                    name="branch"
                    value={formData.branch}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                    required
                  >
                    <option value="">Select Branch</option>
                    {branches.map((branch) => (
                      <option key={branch} value={branch}>{branch}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">Semester *</label>
                  <select
                    name="semester"
                    value={formData.semester}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                    required
                  >
                    <option value="">Select Semester</option>
                    {semesters.map((sem) => (
                      <option key={sem} value={sem}>{sem}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="e.g., Mathematics, Physics"
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">Type</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="PYQ'S">PYQ'S</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2 font-medium">Subtype *</label>
                  <input
                    type="text"
                    name="subtype"
                    value={formData.subtype}
                    onChange={handleInputChange}
                    placeholder="e.g., 2023, syllabus-specific"
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    required
                  />
                </div>

                <div className="md:col-span-2 flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                  >
                    {editingPdf ? 'Update PDF' : 'Add PDF'}
                  </button>
                  {editingPdf && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by year, branch, semester, subject, type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Total PDFs</div>
              <div className="text-2xl font-bold text-white">{pdfs.length}</div>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Filtered</div>
              <div className="text-2xl font-bold text-white">{filteredPdfs.length}</div>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Total Downloads</div>
              <div className="text-2xl font-bold text-white">
                {pdfs.reduce((sum, pdf) => sum + (pdf.downloads || 0), 0)}
              </div>
            </div>
          </div>

          {/* PDF List */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900/50 border-b border-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Year</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Branch</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Sem</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Subject</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Subtype</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Downloads</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredPdfs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-gray-400">
                        No PDFs found
                      </td>
                    </tr>
                  ) : (
                    filteredPdfs.map((pdf) => (
                      <tr key={pdf._id} className="hover:bg-gray-900/30 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-300">{pdf.year}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{pdf.branch}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{pdf.semester}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{pdf.subject}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 bg-indigo-500/20 text-indigo-300 rounded text-xs font-medium">
                            {pdf.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">{pdf.subtype}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{pdf.downloads || 0}</td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(pdf)}
                              className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(pdf._id)}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPdfs;
